import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XCircle, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const PaymentFailure = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get('invoice_id');
  const tenantFromUrl = searchParams.get('tenant');
  const [countdown, setCountdown] = useState(15);
  const [autoRedirect, setAutoRedirect] = useState(true);

  const getRedirectUrl = () => {
    // Check tenant from URL first (most reliable for cross-domain)
    if (tenantFromUrl) {
      return `/p/${tenantFromUrl}/dashboard`;
    }
    
    // Check if customer token exists (general customer portal)
    const customerToken = localStorage.getItem('customer_token');
    if (customerToken) {
      // Check if tenant customer (tenant portal)
      const tenantSlug = localStorage.getItem('tenant_slug') || localStorage.getItem('tenant_code');
      if (tenantSlug) {
        return `/p/${tenantSlug}/dashboard`;
      }
      return '/customer/dashboard';
    }
    
    // Default to admin invoices page
    return '/app/invoices';
  };

  useEffect(() => {
    if (!autoRedirect) return;
    
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(getRedirectUrl());
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, autoRedirect]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center dark:bg-slate-800">
        {/* Failure Icon */}
        <div className="mb-6 flex justify-center">
          <div className="bg-red-100 rounded-full p-6 dark:bg-red-900/40">
            <XCircle className="w-24 h-24 text-red-600 dark:text-red-400" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-800 mb-3 dark:text-slate-200">
          Pembayaran Gagal
        </h1>

        {/* Description */}
        <p className="text-gray-600 mb-6 dark:text-slate-300">
          Maaf, pembayaran Anda tidak dapat diproses. Silakan coba lagi atau hubungi customer service.
        </p>

        {/* Invoice Info */}
        {invoiceId && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 dark:bg-slate-900">
            <p className="text-sm text-gray-500 mb-1 dark:text-slate-400">Invoice ID</p>
            <p className="text-sm font-mono text-gray-700 break-all dark:text-slate-200">{invoiceId}</p>
          </div>
        )}

        {/* Failure Reasons */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 dark:bg-orange-900/30">
          <p className="text-sm font-semibold text-orange-900 mb-2">Kemungkinan Penyebab:</p>
          <ul className="text-left space-y-2 text-sm text-orange-800">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Saldo tidak mencukupi</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Informasi pembayaran tidak valid</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Link pembayaran sudah kadaluwarsa</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Koneksi internet terputus</span>
            </li>
          </ul>
        </div>

        {/* Auto Redirect Info */}
        {autoRedirect && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-6 dark:bg-orange-900/30">
            <p className="text-sm text-gray-700 dark:text-slate-200">
              Auto-redirect dalam <span className="font-bold text-red-600 text-lg dark:text-red-400">{countdown}</span> detik
            </p>
            <button
              onClick={() => setAutoRedirect(false)}
              className="text-xs text-orange-600 hover:text-orange-800 mt-1 underline dark:text-orange-400"
            >
              Batalkan auto-redirect
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button
            onClick={() => navigate(getRedirectUrl())}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold flex items-center justify-center"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Coba Bayar Lagi
          </Button>
          <Button
            onClick={() => navigate(getRedirectUrl())}
            variant="outline"
            className="w-full py-6 text-lg font-semibold"
          >
            🏠 Kembali ke Dashboard
          </Button>
        </div>

        {/* Support Info */}
        <div className="mt-6 pt-6 border-t">
          <p className="text-sm text-gray-600 dark:text-slate-300">
            Butuh bantuan? Hubungi customer service kami
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailure;
